import { FC } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { FileDashedIcon, FileMagnifyingGlassIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';

interface Props {
  /** True when assessments exist but the search/status filter hides them all. */
  filtered?: boolean;
  /** Go to assessments, or clear the filters — decided by the page, which holds the router. */
  onAction: () => void;
}

/**
 * What the remediation queue shows instead of rows.
 *
 * Two facts, not one: an account with nothing remediable yet needs telling how
 * to get something into the queue, while a filtered-to-nothing view only needs
 * its filters back. Sharing one component keeps the two from drifting apart in
 * spacing and tone, the way a pair of hand-rolled blocks would.
 */
export const RemediationListEmptyState: FC<Props> = observer(({ filtered, onAction }) => {
  const { t } = useTrans();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        py: 8,
        px: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.200',
          mb: 0.5,
        }}
      >
        {filtered ? (
          <MagnifyingGlassIcon size={24} color={theme.palette.text.tertiary} />
        ) : (
          <FileDashedIcon size={24} color={theme.palette.text.tertiary} />
        )}
      </Box>

      <Typography variant="textMD_semiBold" color="text.primary">
        {t(
          filtered
            ? 'app.remediation.list.emptyFilter.title'
            : 'app.remediation.list.emptyFirst.title',
        )}
      </Typography>

      <Typography variant="textSM_regular" color="text.tertiary" sx={{ maxWidth: 440 }}>
        {t(
          filtered
            ? 'app.remediation.list.emptyFilter.body'
            : 'app.remediation.list.emptyFirst.body',
        )}
      </Typography>

      <Button
        variant={filtered ? 'secondary' : 'primary'}
        startIcon={filtered ? undefined : <FileMagnifyingGlassIcon size={18} />}
        onClick={onAction}
        sx={{ mt: 1.5 }}
      >
        {t(
          filtered ? 'app.remediation.list.emptyFilter.cta' : 'app.remediation.list.emptyFirst.cta',
        )}
      </Button>
    </Box>
  );
});
